import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useIsDesktop } from "@/hooks/use-is-desktop";
import { useCreateTag } from "@/hooks/use-tag-mutations";
import type { ProjectTag } from "@/types/api";
import {
  TAG_NAME_MAX_LENGTH,
  useCreateTagForm,
} from "./use-create-tag-form";

interface CreateTagSurfaceProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  availableTags: ProjectTag[];
  onCreated: (tag: ProjectTag) => void;
}

export function CreateTagSurface({
  open,
  onOpenChange,
  projectId,
  availableTags,
  onCreated,
}: CreateTagSurfaceProps) {
  const isDesktop = useIsDesktop();
  const createTag = useCreateTag(projectId);
  const form = useCreateTagForm(availableTags);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      form.reset();
      setSubmitError(null);
      createTag.reset();
    }
    // form.reset and createTag.reset are stable for our purposes; rerunning
    // when `open` flips closed is the only behavior we care about.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = () => {
    if (!form.canSubmit || createTag.isPending) return;
    setSubmitError(null);
    createTag.mutate(form.trimmed, {
      onSuccess: (tag) => {
        onCreated(tag);
        onOpenChange(false);
      },
      onError: () => {
        setSubmitError("Could not create tag. Try again.");
      },
    });
  };

  // Block Escape / overlay-click from closing the surface while a create is
  // in flight. Otherwise the surface unmounts but the mutation still resolves,
  // and onSuccess auto-selects a tag the user thought they cancelled.
  const handleOpenChange = (next: boolean) => {
    if (!next && createTag.isPending) return;
    onOpenChange(next);
  };

  const body = (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor="create-tag-input">New tag</Label>
        <Input
          id="create-tag-input"
          autoFocus
          value={form.value}
          onChange={(e) => {
            form.setValue(e.target.value);
            setSubmitError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
          maxLength={TAG_NAME_MAX_LENGTH}
          placeholder="Tag name"
          disabled={createTag.isPending}
          className="text-base"
        />
        <div className="flex items-center justify-between text-xs">
          <span
            className={
              form.isDuplicate ? "text-destructive" : "text-muted-foreground"
            }
          >
            {form.isDuplicate ? "Tag already exists." : " "}
          </span>
          <span className="text-muted-foreground">
            {form.trimmed.length}/{TAG_NAME_MAX_LENGTH}
          </span>
        </div>
        {submitError && (
          <p role="alert" className="text-xs text-destructive">
            {submitError}
          </p>
        )}
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <Button
          type="button"
          variant="ghost"
          onClick={() => onOpenChange(false)}
          disabled={createTag.isPending}
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={submit}
          disabled={!form.canSubmit}
          loading={createTag.isPending}
        >
          Create
        </Button>
      </div>
    </div>
  );

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className="z-[70] sm:max-w-sm"
          overlayClassName="z-[70]"
        >
          <DialogHeader>
            <DialogTitle>Create tag</DialogTitle>
          </DialogHeader>
          {body}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      <DrawerContent className="z-[80]">
        <DrawerHeader>
          <DrawerTitle>Create tag</DrawerTitle>
        </DrawerHeader>
        <div className="p-4 pt-0 pb-6">{body}</div>
      </DrawerContent>
    </Drawer>
  );
}
